import { create, StateCreator } from 'zustand';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

interface AppState {
  // UI State
  isLoading: boolean;
  error: string | null;
  theme: 'light' | 'dark';

  // Modals
  isJobApplicationModalOpen: boolean;
  isPostJobModalOpen: boolean;
  isNotificationModalOpen: boolean;

  // Toasts
  toasts: Toast[];

  // Mobile
  isMobileMenuOpen: boolean;
  isSidebarOpen: boolean;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTheme: (theme: 'light' | 'dark') => void;

  // Modal Actions
  openJobApplicationModal: () => void;
  closeJobApplicationModal: () => void;
  openPostJobModal: () => void;
  closePostJobModal: () => void;
  openNotificationModal: () => void;
  closeNotificationModal: () => void;

  // Toast Actions
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (toastId: string) => void;
  clearToasts: () => void;

  // Mobile Actions
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

// Module-scoped map of toast id -> timer handle so timers can be cleared on
// removeToast/clearToasts and don't leak when toasts are removed early.
const toastTimers = new Map<string, ReturnType<typeof setTimeout>>();

const createAppStore: StateCreator<AppState> = (set, get) => ({
  // Initial State
  isLoading: false,
  error: null,
  theme: 'light',
  isJobApplicationModalOpen: false,
  isPostJobModalOpen: false,
  isNotificationModalOpen: false,
  toasts: [],
  isMobileMenuOpen: false,
  isSidebarOpen: false,

  // Basic Actions
  setLoading: (isLoading: boolean) => set({ isLoading }),

  setError: (error: string | null) => set({ error }),

  setTheme: (theme: 'light' | 'dark') => set({ theme }),

  // Modal Actions
  openJobApplicationModal: () => set({ isJobApplicationModalOpen: true }),

  closeJobApplicationModal: () => set({ isJobApplicationModalOpen: false }),

  openPostJobModal: () => set({ isPostJobModalOpen: true }),

  closePostJobModal: () => set({ isPostJobModalOpen: false }),

  openNotificationModal: () => set({ isNotificationModalOpen: true }),

  closeNotificationModal: () => set({ isNotificationModalOpen: false }),

  // Toast Actions
  addToast: (toast: Omit<Toast, 'id'>) => {
    const id = Date.now().toString();
    const newToast: Toast = { ...toast, id };

    set((state: AppState) => ({
      toasts: [...state.toasts, newToast]
    }));

    // Auto-remove toast after duration. Track handle so it can be cleared.
    const duration = toast.duration || 5000;
    const handle = setTimeout(() => {
      toastTimers.delete(id);
      get().removeToast(id);
    }, duration);
    toastTimers.set(id, handle);
  },

  removeToast: (toastId: string) => {
    const handle = toastTimers.get(toastId);
    if (handle !== undefined) {
      clearTimeout(handle);
      toastTimers.delete(toastId);
    }
    set((state: AppState) => ({
      toasts: state.toasts.filter((toast: Toast) => toast.id !== toastId)
    }));
  },

  clearToasts: () => {
    toastTimers.forEach((handle) => clearTimeout(handle));
    toastTimers.clear();
    set({ toasts: [] });
  },

  // Mobile Actions
  toggleMobileMenu: () => set((state: AppState) => ({
    isMobileMenuOpen: !state.isMobileMenuOpen
  })),

  closeMobileMenu: () => set({ isMobileMenuOpen: false }),

  toggleSidebar: () => set((state: AppState) => ({
    isSidebarOpen: !state.isSidebarOpen
  })),

  closeSidebar: () => set({ isSidebarOpen: false }),
});

export const useAppStore = create<AppState>(createAppStore);
